import React, { useEffect, useState } from 'react';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import { useHistory } from 'react-router-dom';
import { useSettingsContext } from '../../../components/SettingsProvider';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Portal from '@material-ui/core/Portal';
import Typography from '@material-ui/core/Typography';
import CreateFulfilmentLocationDrawer from '../../../components/CreateFulfilmentLocationDrawer';
import EditFulfilmentLocationDrawer from '../../../components/EditFulfilmentLocationDrawer';
import FulfilmentLocationForm from '../../../components/FulfilmentLocationForm';
import { DataGrid } from '@afosto/app-components-react/DataGrid';
import DataGridPreview from '@afosto/app-components-react/DataGrid/DataGridPreview';
import DataList from '@afosto/app-components-react/DataList';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import SkeletonBody from '@afosto/components-react/SkeletonBody';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import getAddressString from '@afosto/utils/getAddressString';
import { fulfilmentLocationFormatter } from './formatters';
import { fulfilment as fulfilmentApi } from '@afosto/fulfilment/api/Fulfilment';
import { addresses as addressesApi } from '@afosto/contacts/api/Addresses';
import translations from './translations';
import { useSnackbar } from 'notistack';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';

const fetchLocations = async dataSource =>
  dataSourceRequest(
    dataSource,
    async (data, requestOptions) =>
      fulfilmentApi.listFulfilmentLocations({ is_disabled: 0 }, requestOptions),
    {
      transformData: async data => {
        try {
          const addressIds = data.reduce((acc, location) => {
            if (location.address_id) {
              return [...acc, location.address_id];
            }

            return acc;
          }, []);

          const addressPromises = [...new Set(addressIds)].map(addressId =>
            addressesApi
              .viewAddress(addressId)
              .then(({ data: address }) => address)
              .catch(() => Promise.resolve(true)),
          );
          const addresses = await Promise.all(addressPromises);

          return data.reduce((acc, fulfilmentLocation) => {
            const address = findObjectInArray(addresses, 'id', fulfilmentLocation.address_id);
            return [...acc, { ...fulfilmentLocation, address }];
          }, []);
        } catch (err) {
          return [];
        }
      },
    },
  );

const DataListContainerGrid = props => <Grid container spacing={1} {...props} />;

const FulfilmentLocations = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions([
    'lcs:locations:read',
    'rel:contacts:read',
  ]);

  const history = useHistory();
  const intl = useIntl();
  const { container, showNewLocationDrawer, setShowNewLocationDrawer } = useSettingsContext();
  const [showEditFulfilmentLocationDrawer, setShowEditFulfilmentLocationDrawer] = useState(false);
  const [location, setLocation] = useState(null);
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();
  const dataSource = useDataGrid(fetchLocations, {
    modules: ['dataProperties', 'activeItem', 'pagination'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'name',
          name: intl.formatMessage({
            id: 'dataGrid.columns.fulfilmentLocation.name',
            defaultMessage: 'Name',
            description: 'The column name for column name',
          }),
          type: 'fulfilmentLocation',
        },
        {
          key: 'created_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.fulfilmentLocation.createdAt',
            defaultMessage: 'Created at',
            description: 'The column name for column created at',
          }),
          type: 'datetime',
        },
      ],
    },
  });
  const { activeItem: activeItemModule = {} } = dataSource;
  const { activeItem, setActiveItem } = activeItemModule;

  const handleDeleteFulfilmentLocation = async locationID => {
    try {
      const confirmResponse = await confirm({
        title: intl.formatMessage(translations.confirmDelete.title),
        description: intl.formatMessage(translations.confirmDelete.description),
        catchOnCancel: false,
        buttons: [
          {
            label: intl.formatMessage(translations.confirmDelete.buttons.cancel),
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage(translations.confirmDelete.buttons.delete),
            response: 'delete',
            type: 'submit',
            variant: 'contained',
            color: 'primary',
          },
        ],
      });

      if (confirmResponse === 'delete') {
        await fulfilmentApi.updateFulfilmentLocation(locationID, {
          name: location.name,
          address_id: location.address_id,
          is_disabled: true,
        });
        setActiveItem(null);
        dataSource.reloadDataSource();
        enqueueSnackbar(intl.formatMessage(translations.feedback.fulfilmentLocationDeleted), {
          variant: 'success',
        });
      }
    } catch (error) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.fulfilmentLocationNotDeleted), {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchLocation = async () => {
      if (isMounted) {
        setIsFetchingLocation(true);
      }

      try {
        const response = await fulfilmentApi.getFulfilmentLocation(activeItem);
        const { data } = response;

        if (data?.address_id) {
          const addressResponse = await addressesApi.viewAddress(data.address_id).catch(() => null);
          const { data: address } = addressResponse ?? {};

          if (isMounted) {
            setLocation({
              ...data,
              address: address
                ? {
                    ...address,
                    label: `${address.address_line_1} ${address.postal_code} ${address.locality} ${address.country_code}`,
                    title: `${address.address_line_1} ${address.postal_code} ${address.locality} ${address.country_code}`,
                    description: `${address.given_name} ${address.family_name} ${address.organisation}`,
                    value: address.id,
                  }
                : null,
            });
          }
        } else if (isMounted) {
          setLocation(data);
        }
      } catch (err) {
        if (isMounted) {
          setLocation(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingLocation(false);
        }
      }
    };

    if (activeItem) {
      fetchLocation();
    } else if (isMounted) {
      setLocation(null);
    }

    return () => {
      isMounted = false;
    };
  }, [activeItem]);

  /**
   * Handler after successfully creating a new fulfilment location.
   */
  const handleSuccessCreateFulfilmentLocation = () => {
    setShowNewLocationDrawer(false);
    dataSource.reloadDataSource();
  };

  /**
   * Update the fulfilment location.
   */
  const handleUpdateLocation = updatedLocation => {
    setShowEditFulfilmentLocationDrawer(false);
    setLocation(updatedLocation);
    dataSource.reloadDataSource();
  };

  /**
   * Render a fulfilment location in the data list.
   * @param {object} fulfilmentLocation
   * @returns {*}
   */
  const renderFulfilmentLocation = fulfilmentLocation => (
    <Grid key={fulfilmentLocation.id} item xs={12}>
      <Card>
        <CardActionArea
          onClick={() => history.push(`/settings/fulfilment-locations/${fulfilmentLocation.id}`)}
        >
          <CardContent>
            <Typography variant="body1">{fulfilmentLocation.name}</Typography>
            <Typography variant="body2" color="textSecondary">
              {fulfilmentLocation.address
                ? getAddressString(fulfilmentLocation.address)
                : intl.formatMessage(translations.page.noAddressMessage)}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  );

  /**
   * Render the skeleton of an fulfilment location in the data list.
   * @param {string} key
   * @returns {*}
   */
  const renderFulfilmentLocationSkeleton = key => (
    <Grid key={key} item xs={12}>
      <Card>
        <CardContent>
          <SkeletonBody lines={2} />
        </CardContent>
      </Card>
    </Grid>
  );

  if (!hasPermissions()) {
    return <UnauthorizedComponent />;
  }

  const previewMenu = [
    ...(location
      ? [
          {
            key: 'fulfilment_location_view',
            translationKey: 'actions.view',
            label: 'View',
            icon: 'eye',
            shortcut: true,
            onClick: () => history.push(`/settings/fulfilment-locations/${location.id}`),
          },
          ...(hasPermissions(['lcs:locations:write'])
            ? [
                {
                  key: 'fulfilment_location_edit',
                  translationKey: 'actions.edit',
                  label: 'Edit',
                  icon: 'pencil',
                  shortcut: true,
                  onClick: () => setShowEditFulfilmentLocationDrawer(true),
                },
                {
                  key: 'fulfilment_location_delete',
                  translationKey: 'actions.delete',
                  label: 'Delete',
                  icon: 'trash-alt',
                  onClick: () => handleDeleteFulfilmentLocation(location.id),
                  color: 'secondary',
                },
              ]
            : []),
        ]
      : []),
  ];

  return (
    <>
      <Hidden implementation="js" mdUp>
        <DataList
          dataSource={dataSource}
          renderItem={renderFulfilmentLocation}
          renderSkeletonItem={renderFulfilmentLocationSkeleton}
          component={DataListContainerGrid}
          emptyMessage={intl.formatMessage(translations.page.emptyMessage)}
        />
      </Hidden>
      <Hidden implementation="js" smDown>
        <DataGrid
          dataSource={dataSource}
          formatters={{ fulfilmentLocation: fulfilmentLocationFormatter }}
        />
      </Hidden>
      <Portal container={container.current}>
        <DataGridPreview
          menu={previewMenu}
          onClose={() => dataSource.activeItem.setActiveItem(null)}
          open={dataSource.activeItem.activeItem !== null}
          title={location?.name}
        >
          <LoadingWrapper isLoading={isFetchingLocation}>
            {location && <FulfilmentLocationForm location={location} inline />}
          </LoadingWrapper>
        </DataGridPreview>
        <CreateFulfilmentLocationDrawer
          open={showNewLocationDrawer}
          onSuccess={handleSuccessCreateFulfilmentLocation}
          onClose={() => setShowNewLocationDrawer(false)}
        />
        <EditFulfilmentLocationDrawer
          location={location}
          onSuccess={handleUpdateLocation}
          onClose={() => setShowEditFulfilmentLocationDrawer(false)}
          open={showEditFulfilmentLocationDrawer}
        />
      </Portal>
    </>
  );
};

export default FulfilmentLocations;
